import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useEntitiesByUuid } from "@hooks/useEntities";
import {
    isSuggestedSendingEmailRow,
    type SendingEmailRow,
} from "../sendingEmailsViewConfig";

// Read-only: this column only ever displays the entities a sender is already
// scoped to (edited via the Edit modal's entity picker, not inline). An empty
// `entityUuids` means the address is unscoped - available to every entity in
// the workspace - so the cell shows nothing rather than every entity icon.
export const SendingEmailEntitiesCell = memo(
    ({ row }: CellContext<ViewTableFeatures, SendingEmailRow, string>) => {
        const entitiesByUuid = useEntitiesByUuid();
        const email = row.original;

        const entities = useMemo(() => {
            if (isSuggestedSendingEmailRow(email)) {
                return [];
            }
            return email.entityUuids
                .map((uuid) => entitiesByUuid.get(uuid))
                .filter((entity): entity is NonNullable<typeof entity> =>
                    Boolean(entity),
                );
        }, [email, entitiesByUuid]);

        if (entities.length === 0) {
            return <ViewTableCell>-</ViewTableCell>;
        }

        return (
            <ViewTableCell>
                <EntityAvatarGroup entities={entities} max={3} size="xs" />
            </ViewTableCell>
        );
    },
);
